import React, { useEffect, useRef, useState } from "react";
import AppShell from "@/components/AppShell";
import { api, formatApiError, API } from "@/lib/api";
import { UploadSimple, Trash, Plus, Sparkle } from "@phosphor-icons/react";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";

const CATEGORY_LABELS = {
  top: "Top", bottom: "Bottom", dress: "Dress", shoes: "Shoes",
  accessory: "Accessory", outerwear: "Outerwear", bag: "Bag", unknown: "Unsorted",
};

export default function Wardrobe() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const fileRef = useRef(null);
  const [filter, setFilter] = useState("all");

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/wardrobe");
      setItems(data);
    } catch (e) { /* ignore */ }
    setLoading(false);
  };

  useEffect(() => { load(); }, []);

  const onFiles = async (files) => {
    if (!files?.length) return;
    setUploading(true);
    const total = files.length;
    let done = 0;
    for (const f of files) {
      try {
        const fd = new FormData();
        fd.append("file", f);
        const { data } = await api.post("/wardrobe/upload", fd, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        setItems((prev) => [data, ...prev]);
        done += 1;
        toast.success(`Added: ${data.description || data.category} (${done}/${total})`);
      } catch (e) {
        toast.error(formatApiError(e.response?.data?.detail) || "Upload failed");
      }
    }
    setUploading(false);
  };

  const remove = async (id) => {
    try {
      await api.delete(`/wardrobe/${id}`);
      setItems((prev) => prev.filter((i) => i.id !== id));
      toast.success("Removed");
    } catch (e) { toast.error("Could not delete"); }
  };

  const categories = ["all", ...Array.from(new Set(items.map((i) => i.category || "unknown")))];
  const filtered = filter === "all" ? items : items.filter((i) => i.category === filter);

  const token = typeof window !== "undefined" ? localStorage.getItem("stylist_token") : null;
  const imgSrc = (path) => `${API}/files/${path}${token ? `?auth=${token}` : ""}`;

  return (
    <AppShell>
      <div className="flex flex-col sm:flex-row sm:items-end justify-between gap-6">
        <div>
          <p className="text-[0.7rem] tracking-[0.3em] uppercase text-charcoal-500">Your digital closet</p>
          <h1 className="font-serif text-4xl sm:text-5xl lg:text-6xl mt-3 text-charcoal-900 tracking-tight">Wardrobe</h1>
          <p className="mt-3 text-charcoal-600 max-w-xl">Ava auto-tags every piece you upload so she can style you from what you already own.</p>
        </div>
        <div>
          <input
            type="file" accept="image/jpeg,image/png,image/webp" multiple ref={fileRef}
            data-testid="wardrobe-file-input"
            className="hidden"
            onChange={(e) => onFiles(Array.from(e.target.files || []))}
          />
          <button
            onClick={() => fileRef.current?.click()}
            disabled={uploading}
            data-testid="wardrobe-upload-button"
            className="btn-primary-pill disabled:opacity-60"
          >
            {uploading ? "Analyzing…" : <><Plus size={16} className="mr-2" /> Upload items</>}
          </button>
        </div>
      </div>

      {/* Filter pills */}
      <div className="mt-8 flex flex-wrap gap-2">
        {categories.map((c) => (
          <button
            key={c}
            onClick={() => setFilter(c)}
            data-testid={`filter-${c}`}
            className={`rounded-full px-4 py-1.5 text-xs tracking-widest uppercase transition-all ${
              filter === c ? "bg-charcoal-700 text-white" : "bg-cream-100 text-charcoal-700 border border-cream-200"
            }`}
          >
            {c === "all" ? `All · ${items.length}` : CATEGORY_LABELS[c] || c}
          </button>
        ))}
      </div>

      {/* Grid */}
      {loading ? (
        <div className="mt-16 text-center text-charcoal-500 font-serif italic text-2xl">Loading your closet…</div>
      ) : filtered.length === 0 ? (
        <EmptyState onClick={() => fileRef.current?.click()} />
      ) : (
        <div className="mt-8 grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-5">
          <AnimatePresence>
            {filtered.map((item) => (
              <motion.div
                key={item.id}
                layout
                initial={{ opacity: 0, y: 15 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0, scale: 0.95 }}
                className="group rounded-3xl overflow-hidden border border-cream-200 bg-cream-100 transition-all hover:-translate-y-1 hover:shadow-md"
                data-testid={`wardrobe-item-${item.id}`}
              >
                <div className="aspect-[3/4] relative overflow-hidden bg-white">
                  <img src={imgSrc(item.storage_path)} alt={item.description || item.category} className="w-full h-full object-cover" />
                  <button
                    onClick={() => remove(item.id)}
                    data-testid={`delete-item-${item.id}`}
                    className="absolute top-3 right-3 rounded-full p-2 bg-cream-50/90 border border-cream-200 text-charcoal-700 opacity-0 group-hover:opacity-100 transition-opacity hover:bg-white"
                    aria-label="Delete"
                  >
                    <Trash size={14} />
                  </button>
                </div>
                <div className="p-4">
                  <p className="text-[0.6rem] tracking-[0.3em] uppercase text-charcoal-500">
                    {CATEGORY_LABELS[item.category] || item.category}
                  </p>
                  <p className="text-sm mt-1 text-charcoal-800 line-clamp-2">{item.description || "—"}</p>
                  <div className="mt-3 flex flex-wrap gap-1.5">
                    {(item.colors || []).slice(0, 3).map((c) => (
                      <span key={c} className="tag-chip">{c}</span>
                    ))}
                    {(item.style_tags || []).slice(0, 2).map((c) => (
                      <span key={c} className="tag-chip">{c}</span>
                    ))}
                  </div>
                </div>
              </motion.div>
            ))}
          </AnimatePresence>
        </div>
      )}
    </AppShell>
  );
}

function EmptyState({ onClick }) {
  return (
    <div className="mt-16 rounded-3xl border border-dashed border-cream-200 bg-cream-100 p-16 text-center">
      <Sparkle size={36} weight="thin" className="mx-auto text-charcoal-700" />
      <h3 className="font-serif text-3xl mt-4">Your closet is a blank canvas.</h3>
      <p className="text-charcoal-600 mt-3 max-w-md mx-auto">Upload a few photos — tops, bottoms, shoes — and Ava will start building looks just for you.</p>
      <button onClick={onClick} data-testid="empty-upload-button" className="btn-primary-pill mt-8">
        <UploadSimple size={16} className="mr-2" /> Upload your first pieces
      </button>
    </div>
  );
}
